import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SwitchTransition, Transition } from 'react-transition-group'

import { useAppLayout } from '@/components/AppLayout/AppLayoutProvider'
import ConversationBackground, {
  ConversationBackgroundType,
  ConversationChoiceType,
} from '@/components/Chat/ConversationBackground'
import Decoration from '@/components/Decoration/Decoration'
import { useAppState } from '@/composables/useAppState'
import { useGSAP } from '@/composables/useGSAP'
import useTracking from '@/composables/useTracking'
import { getSurveyQuestionKey } from '@/services/Survey'
import { questions } from '@/state/AppConfig'

import QuestionsBinary from './QuestionsBinary'
import QuestionsBinaryExplanations from './QuestionsBinaryExplanations'
import QuestionsFeedback from './QuestionsFeedback'
import QuestionsLikert from './QuestionsLikert'
import QuestionsPreliminary from './QuestionsPreliminary'

export default function QuestionList({ className }: React.HTMLProps<HTMLDivElement>) {
  const { actor, context, persona, scenario, state, scenarioUnitName } = useAppState()
  const { root } = useAppLayout()

  const { t } = useTranslation()
  const { trackImpression, trackClick } = useTracking()

  const { gsap } = useGSAP()

  const isPreliminary = state.matches('preliminary')
  const isBefore = state.matches('questions.before')
  const isAfter = state.matches('questions.after')

  let answers = {}
  let subject = ''
  let questionState = 'preliminary'
  let config: string[] = []
  if (!isPreliminary) {
    if (isBefore) {
      answers = context.answers.before
      questionState = 'before'
      config = context.relationship === 'CHILD' ? questions.before_long : questions.before
    } else if (isAfter) {
      answers = context.answers.after
      questionState = 'after'
      config = context.relationship === 'CHILD' ? questions.after_long : questions.after
    }
  }

  const questionIndex = Object.keys(answers).length + 1
  const questionType = isPreliminary ? 'preliminary' : config[questionIndex - 1]
  subject = isPreliminary ? 'preliminary' : `${questionState}_${questionIndex}`
  const nbBinary = config.filter((value) => value === 'binary').length
  const nbBinaryExplanation = config.filter((value) => value === 'binary_explanation').length
  const nbLikert = config.filter((value) => value === 'likert').length

  const backgroundType = isPreliminary
    ? 'neutral'
    : ((questionType === 'binary' ? `binary_${questionIndex}` : `none`) as ConversationBackgroundType)

  const unitVariation = useMemo(() => {
    if (isPreliminary && questionType === 'preliminary') return 'intro_audience_page'

    return `${questionState === 'before' ? 'intro' : 'outro'}_survey_${questionType === 'likert' ? questionIndex - nbBinaryExplanation : questionIndex}`
  }, [isPreliminary, questionState, questionType, questionIndex, nbBinaryExplanation])

  const onQuestionsChoose = useCallback(
    (answer: string) => {
      const index = questionType === 'likert' ? questionIndex - nbBinary - nbBinaryExplanation : questionIndex
      const questionKey = getSurveyQuestionKey(
        `${persona}.${scenario.current}.${questionState}.${questionType}.${index}`
      )

      if (isPreliminary || questionType === 'binary' || questionType === 'likert') {
        trackClick({
          unitName: `${persona}_simulation${scenarioUnitName}`,
          unitVariation,
          unitLocation: 'tyce',
          clickType: 'button',
          buttonText: isPreliminary ? `${answer}` : `${questionKey}`,
          buttonName: isPreliminary ? 'navigation_button' : null,
        })
      }
      if (isPreliminary) {
        // Here we catch the relationship and send it to web
        root.current?.dispatchEvent(
          new CustomEvent('simulation:pick_relationship', { detail: answer, bubbles: true, composed: true })
        )
      }
      actor.send({ type: 'CHOOSE', persona, question: subject, questionKey, answer })
    },
    [
      persona,
      scenario,
      subject,
      actor,
      trackClick,
      questionState,
      questionType,
      questionIndex,
      nbBinary,
      nbBinaryExplanation,
      unitVariation,
      isPreliminary,
      scenarioUnitName,
      root,
    ]
  )

  useEffect(() => {
    if ((isPreliminary && questionType === 'preliminary') || questionType === 'binary' || questionType === 'likert') {
      trackImpression({
        unitName: `${persona}_simulation${scenarioUnitName}`,
        unitLocation: 'tyce',
        unitVariation,
      })
    }
  }, [unitVariation, questionType, isPreliminary])

  const isVisible = state.matches('questions') || state.matches('preliminary')
  const questionRoot = useRef<HTMLDivElement>(null)

  const [appearProfile, setAppearProfile] = useState<boolean>(true)

  const onEnter = useCallback(() => {
    const ctx = gsap.context(() => {
      gsap.timeline().add(() => setAppearProfile(false), 0.7)
    })

    return () => ctx.kill()
  }, [gsap, setAppearProfile])

  const onExit = useCallback(() => {
    const ctx = gsap.context(() => {
      gsap
        .timeline()
        .fromTo(
          questionRoot.current,
          {
            opacity: 1,
          },
          {
            opacity: 0,
            duration: 0.5,
            ease: 'power2.out',
          },
          0
        )
        .add(() => setAppearProfile(true))
    })

    return () => ctx.kill()
  }, [gsap, questionRoot, setAppearProfile])

  const [choices, setChoices] = useState<ConversationChoiceType[]>([])
  useEffect(() => {
    switch (questionType) {
      case 'binary':
        setChoices([
          {
            i18nKey: `questions.true`,
            ariaLabelI18nKey: `questions.true`,
            value: 'true',
          },
          {
            i18nKey: `questions.false`,
            ariaLabelI18nKey: `questions.false`,
            value: 'false',
          },
        ])
        break
      case 'feedback':
        setChoices([
          {
            i18nKey: `<good />`,
            ariaLabelI18nKey: `questions.good`,
            value: '0',
          },
          {
            i18nKey: `<neutral />`,
            ariaLabelI18nKey: `questions.neutral`,
            value: '1',
          },
          {
            i18nKey: `<bad />`,
            ariaLabelI18nKey: `questions.bad`,
            value: '2',
          },
        ])
        break
      default:
        setChoices([])
        break
    }
  }, [questionType])

  const backgroundRef = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    const root = questionRoot.current
    if (root === null || !isVisible) return
    const tween = gsap.to(window, { scrollTo: { y: 0 }, duration: 0.5, ease: 'power2.out' })
    const timeout = setTimeout(() => {
      // Intentionally no focus action here.
      // const focusStarts = [...root.querySelectorAll('.FocusStart')] as HTMLElement[]
      // focusStarts[focusStarts.length -1 ]?.focus({ preventScroll: true });
    }, 500)

    return () => {
      tween.kill()
      clearTimeout(timeout)
    }
  }, [isVisible, questionType, questionIndex, questionRoot, gsap])

  return (
    <Transition
      in={isVisible}
      nodeRef={questionRoot}
      onEnter={onEnter}
      onExit={onExit}
      timeout={{
        enter: 700,
        exit: 150,
      }}
      appear
      mountOnEnter
      unmountOnExit
    >
      <div
        data-testid='questions'
        ref={questionRoot}
        className='col-span-1 col-start-1 row-span-1 row-start-1 max-md:flex max-md:flex-col'
      >
        {questionType === 'preliminary' && (
          <>
            <QuestionsPreliminary onChoose={onQuestionsChoose} className={className} appearProfile={appearProfile} />
            <Decoration show />
          </>
        )}
        {questionType === 'binary' && (
          <>
            <QuestionsBinary
              index={questionIndex}
              max={nbBinary}
              introduction={`questions.${persona}.${scenario.current}.${questionState}.binary.introduction`}
              question={`questions.${persona}.${scenario.current}.${questionState}.binary.${questionIndex}`}
              className={className}
              appearProfile={appearProfile}
              choices={choices}
              onChoose={onQuestionsChoose}
            />
            <Decoration show noAnimation />
          </>
        )}
        {questionType === 'feedback' && (
          <QuestionsFeedback
            className={className}
            question={t(
              `questions.${persona}.${scenario.current}.${questionState}.feedback.${questionIndex - nbBinary}`
            )}
            appearProfile={appearProfile}
          />
        )}

        {isAfter && (
          <QuestionsBinaryExplanations
            className={className}
            max={nbBinary}
            questionState={questionState}
            onChoose={onQuestionsChoose}
            isVisible={questionType === 'binary_explanation'}
            scenario={scenario.current}
          />
        )}

        <QuestionsLikert
          index={questionIndex - nbBinary - nbBinaryExplanation}
          max={nbLikert}
          className={className}
          question={t(
            `questions.${persona}.${scenario.current}.${questionState}.likert.${questionIndex - nbBinary - nbBinaryExplanation}`
          )}
          onChoose={onQuestionsChoose}
          isVisible={questionType === 'likert'}
        />

        {questionType !== 'binary' && questionType !== 'preliminary' && (
          <SwitchTransition mode='out-in'>
            <Transition
              key={`${questionType}_${questionIndex}`}
              appear
              mountOnEnter
              unmountOnExit
              nodeRef={backgroundRef}
              timeout={{
                enter: 700,
                exit: 500,
              }}
            >
              {(state) => (
                <ConversationBackground
                  ref={backgroundRef}
                  type={backgroundType}
                  subject={subject}
                  choices={choices}
                  onChoose={onQuestionsChoose}
                  isLeaving={state === 'exiting'}
                />
              )}
            </Transition>
          </SwitchTransition>
        )}
      </div>
    </Transition>
  )
}
